/**
 * IconPicker - A shared component for selecting icons (Lucide icons + emojis)
 * Used for both project and board customization
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { clsx } from 'clsx';
import {
  FolderKanban,
  LayoutGrid,
  Kanban,
  ListTodo,
  Calendar,
  Table,
  ClipboardList,
  Rocket,
  Target,
  Zap,
  Star,
  Heart,
  Flag,
  Bookmark,
  Bell,
  Settings,
  Users,
  Home,
  Globe,
  Briefcase,
  Code,
  Database,
  FileText,
  Image,
  Music,
  Video,
  ShoppingCart,
  CreditCard,
  Truck,
  Package,
  Gift,
  Coffee,
  Lightbulb,
  type LucideIcon,
} from 'lucide-react';

// Lucide icon options with categories
export const lucideIconOptions = [
  // Project/Board
  { id: 'folder-kanban', icon: FolderKanban, label: 'Kanban Folder' },
  { id: 'layout-grid', icon: LayoutGrid, label: 'Grid' },
  { id: 'kanban', icon: Kanban, label: 'Kanban' },
  { id: 'list-todo', icon: ListTodo, label: 'List' },
  { id: 'calendar', icon: Calendar, label: 'Calendar' },
  { id: 'table', icon: Table, label: 'Table' },
  { id: 'clipboard-list', icon: ClipboardList, label: 'Clipboard' },
  // Action/Status
  { id: 'rocket', icon: Rocket, label: 'Rocket' },
  { id: 'target', icon: Target, label: 'Target' },
  { id: 'zap', icon: Zap, label: 'Zap' },
  { id: 'star', icon: Star, label: 'Star' },
  { id: 'heart', icon: Heart, label: 'Heart' },
  { id: 'flag', icon: Flag, label: 'Flag' },
  { id: 'bookmark', icon: Bookmark, label: 'Bookmark' },
  { id: 'bell', icon: Bell, label: 'Bell' },
  { id: 'lightbulb', icon: Lightbulb, label: 'Lightbulb' },
  // Business
  { id: 'settings', icon: Settings, label: 'Settings' },
  { id: 'users', icon: Users, label: 'Users' },
  { id: 'home', icon: Home, label: 'Home' },
  { id: 'globe', icon: Globe, label: 'Globe' },
  { id: 'briefcase', icon: Briefcase, label: 'Briefcase' },
  // Tech
  { id: 'code', icon: Code, label: 'Code' },
  { id: 'database', icon: Database, label: 'Database' },
  { id: 'file-text', icon: FileText, label: 'Document' },
  // Media
  { id: 'image', icon: Image, label: 'Image' },
  { id: 'music', icon: Music, label: 'Music' },
  { id: 'video', icon: Video, label: 'Video' },
  // Commerce
  { id: 'shopping-cart', icon: ShoppingCart, label: 'Shopping' },
  { id: 'credit-card', icon: CreditCard, label: 'Payment' },
  { id: 'truck', icon: Truck, label: 'Shipping' },
  { id: 'package', icon: Package, label: 'Package' },
  { id: 'gift', icon: Gift, label: 'Gift' },
  { id: 'coffee', icon: Coffee, label: 'Coffee' },
] as const;

// Emoji options organized by category
export const emojiOptions = [
  // Objects & Activities
  '📁', '📂', '📋', '📝', '📊', '📈', '📉', '📅',
  // Status & Symbols
  '🎯', '🚀', '⭐', '❤️', '🔥', '💡', '⚡', '🎉',
  // Nature & Weather
  '🌟', '🌈', '☀️', '🌙', '🌊', '🌸', '🍀', '🌿',
  // Tech & Business
  '💻', '🖥️', '📱', '💼', '🏢', '🏠', '🌍', '🔧',
  // Food & Drink
  '☕', '🍕', '🍔', '🎂', '🍎', '🥗', '🍜', '🍩',
  // Animals
  '🦄', '🐱', '🐶', '🦊', '🐻', '🐼', '🦁', '🐸',
  // Faces & Gestures
  '😊', '🤔', '💪', '👍', '✅', '🎨', '🎵', '🎮',
  // Sports & Games
  '⚽', '🏀', '🎾', '🏆', '🎲', '♟️', '🎸', '🎭',
];

export type IconValue = string; // Can be lucide icon id or emoji

export function isEmoji(value: string): boolean {
  // Check if the value is an emoji (not a lucide icon id)
  return !lucideIconOptions.some((opt) => opt.id === value);
}

export function getLucideIcon(iconId: string): LucideIcon | null {
  const found = lucideIconOptions.find((opt) => opt.id === iconId);
  return found?.icon || null;
}

interface IconPickerProps {
  value: IconValue;
  onChange: (value: IconValue) => void;
  color?: string | undefined;
  size?: 'sm' | 'md' | 'lg';
}

export function IconPicker({ value, onChange, color = '#3B82F6', size = 'md' }: IconPickerProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'icons' | 'emojis'>(() =>
    isEmoji(value) ? 'emojis' : 'icons'
  );

  const sizeClasses = {
    sm: { preview: 'h-12 w-12', icon: 'h-6 w-6', text: 'text-xl', grid: 'h-7 w-7', gridIcon: 'h-3.5 w-3.5', gridText: 'text-base' },
    md: { preview: 'h-16 w-16', icon: 'h-8 w-8', text: 'text-2xl', grid: 'h-8 w-8', gridIcon: 'h-4 w-4', gridText: 'text-lg' },
    lg: { preview: 'h-20 w-20', icon: 'h-10 w-10', text: 'text-3xl', grid: 'h-9 w-9', gridIcon: 'h-5 w-5', gridText: 'text-xl' },
  };

  const sizes = sizeClasses[size];
  const isCurrentEmoji = isEmoji(value);
  const CurrentLucideIcon = !isCurrentEmoji ? getLucideIcon(value) : null;

  return (
    <div className="space-y-3">
      {/* Preview */}
      <div className="flex items-start gap-4">
        <div
          className={clsx(
            sizes.preview,
            'flex items-center justify-center rounded-xl text-white'
          )}
          style={{ backgroundColor: color }}
        >
          {isCurrentEmoji ? (
            <span className={sizes.text}>{value}</span>
          ) : CurrentLucideIcon ? (
            <CurrentLucideIcon className={sizes.icon} />
          ) : (
            <FolderKanban className={sizes.icon} />
          )}
        </div>

        <div className="flex-1">
          {/* Tabs */}
          <div className="flex gap-1 rounded-lg bg-neutral-100 p-1 dark:bg-neutral-800">
            <button
              type="button"
              onClick={() => setActiveTab('icons')}
              className={clsx(
                'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                activeTab === 'icons'
                  ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-white'
                  : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              {t('common.icons')}
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('emojis')}
              className={clsx(
                'flex-1 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                activeTab === 'emojis'
                  ? 'bg-white text-neutral-900 shadow-sm dark:bg-neutral-700 dark:text-white'
                  : 'text-neutral-500 hover:text-neutral-700 dark:hover:text-neutral-300'
              )}
            >
              {t('common.emojis')}
            </button>
          </div>

          {/* Icon/Emoji Grid */}
          <div className="mt-2 max-h-32 overflow-y-auto rounded-lg border border-neutral-200 bg-neutral-50 p-2 dark:border-neutral-700 dark:bg-neutral-800/50">
            {activeTab === 'icons' ? (
              <div className="grid grid-cols-8 gap-1">
                {lucideIconOptions.map((opt) => {
                  const IconComp = opt.icon;
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => onChange(opt.id)}
                      title={opt.label}
                      className={clsx(
                        'flex items-center justify-center rounded-md transition-colors',
                        sizes.grid,
                        value === opt.id
                          ? 'bg-primary-100 text-primary-600 dark:bg-primary-900/30 dark:text-primary-400'
                          : 'text-neutral-500 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-neutral-700 dark:hover:text-neutral-300'
                      )}
                    >
                      <IconComp className={sizes.gridIcon} />
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="grid grid-cols-8 gap-1">
                {emojiOptions.map((emoji) => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => onChange(emoji)}
                    className={clsx(
                      'flex items-center justify-center rounded-md transition-colors',
                      sizes.grid,
                      value === emoji
                        ? 'bg-primary-100 dark:bg-primary-900/30'
                        : 'hover:bg-neutral-200 dark:hover:bg-neutral-700'
                    )}
                  >
                    <span className={sizes.gridText}>{emoji}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Color picker component (shared)
export const colorOptions = [
  '#3B82F6', // blue
  '#10B981', // emerald
  '#8B5CF6', // violet
  '#F59E0B', // amber
  '#EF4444', // red
  '#EC4899', // pink
  '#6366F1', // indigo
  '#14B8A6', // teal
  '#84CC16', // lime
  '#F97316', // orange
  '#06B6D4', // cyan
  '#A855F7', // purple
];

// i18n key suffix per swatch (common.colors.*) so screen readers announce
// a color name instead of an anonymous button
const colorNameKeys: Record<string, string> = {
  '#3B82F6': 'blue',
  '#10B981': 'emerald',
  '#8B5CF6': 'violet',
  '#F59E0B': 'amber',
  '#EF4444': 'red',
  '#EC4899': 'pink',
  '#6366F1': 'indigo',
  '#14B8A6': 'teal',
  '#84CC16': 'lime',
  '#F97316': 'orange',
  '#06B6D4': 'cyan',
  '#A855F7': 'purple',
};

interface ColorPickerProps {
  value: string | undefined;
  onChange: (value: string) => void;
}

export function ColorPicker({ value, onChange }: ColorPickerProps) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      {colorOptions.map((color) => (
        <button
          key={color}
          type="button"
          onClick={() => onChange(color)}
          aria-label={t(`common.colors.${colorNameKeys[color]}`)}
          aria-pressed={value === color}
          className={clsx(
            'h-6 w-6 rounded-full transition-transform',
            value === color && 'ring-2 ring-offset-2 ring-primary-500'
          )}
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );
}

export default IconPicker;
